import {
  createMemoryStorage,
  openIndexedDbStorage,
  type ImportSummary,
  type PortableDataset,
  type StorageProvider,
} from "@tchoma/storage";
import {
  INBOX_DOCUMENT_TYPE,
  attachmentRef,
  createInboxEntryData,
  matchesInboxQuery,
  parseInboxEntryData,
  type InboxCaptureDraft,
  type InboxEntry,
} from "./model";

export const DELETE_ALL_CONFIRMATION = "DELETE ALL INBOX DATA";
export type InboxImportPolicy = "merge" | "replace";

export interface InboxOwnershipSummary {
  documents: number;
  liveEntries: number;
  tombstones: number;
  outboxOperations: number;
  blobs: number;
  blobBytes: number;
}

export interface InboxImportPreview extends InboxOwnershipSummary {
  exportedAt: string;
  collisions: number;
  orphanBlobs: number;
}

export interface InboxImportResult {
  policy: InboxImportPolicy;
  preview: InboxImportPreview;
  imported: ImportSummary;
  skippedCollisions: number;
}

export interface InboxDeleteAllResult {
  recoveryDataset: PortableDataset;
  removed: InboxOwnershipSummary;
  cleared: boolean;
}

export class InboxRepository {
  constructor(private readonly storage: StorageProvider) {}

  async capture(draft: InboxCaptureDraft): Promise<InboxEntry> {
    const text = draft.text?.trim() ?? "";
    if (!text && !draft.file) throw new Error("Add text, a URL, an image or a file.");
    const blob = draft.file ? await this.storage.blobs.put(draft.file, { mime: draft.file.type || "application/octet-stream" }) : null;
    try {
      return await this.storage.documents.put<InboxEntry["data"]>({
        id: crypto.randomUUID(),
        type: INBOX_DOCUMENT_TYPE,
        data: createInboxEntryData({ ...draft, text }, blob),
      }, { expectedVersion: null });
    } catch (error) {
      if (blob) await this.storage.blobs.delete(blob).catch(() => undefined);
      throw error;
    }
  }

  async list(options: { archived?: boolean; query?: string } = {}): Promise<InboxEntry[]> {
    const documents = await this.storage.documents.list<InboxEntry["data"]>({
      type: INBOX_DOCUMENT_TYPE,
      limit: 10_000,
    });
    return documents
      .filter((entry) => entry.data.archived === (options.archived ?? false))
      .filter((entry) => matchesInboxQuery(entry, options.query ?? ""))
      .sort((left, right) => right.createdAt.localeCompare(left.createdAt));
  }

  async setArchived(entry: InboxEntry, archived: boolean): Promise<InboxEntry> {
    return this.storage.documents.put<InboxEntry["data"]>({
      id: entry.id,
      type: entry.type,
      data: { ...entry.data, archived },
    }, { expectedVersion: entry.version });
  }

  async remove(entry: InboxEntry): Promise<void> {
    await this.storage.documents.delete(entry.type, entry.id, { expectedVersion: entry.version });
    const attachment = entry.data.attachment;
    if (attachment) {
      await this.storage.blobs.delete({
        id: attachment.blobId,
        mime: attachment.mime,
        size: attachment.size,
        hash: attachment.hash,
        createdAt: attachment.createdAt,
      }).catch(() => undefined);
    }
  }

  async exportDataset(): Promise<PortableDataset> {
    return this.storage.exportDataset();
  }

  async previewImport(datasetInput: unknown): Promise<InboxImportPreview> {
    const candidate = cloneImportInput(datasetInput);
    const validator = createMemoryStorage({ mode: "local-only" });
    try {
      await validator.importDataset(candidate, { mode: "replace" });
    } finally {
      validator.close();
    }
    const dataset = candidate as PortableDataset;
    if (dataset.mode !== "local-only" || dataset.outbox.length !== 0) {
      throw new Error("Inbox imports must come from a local-only Inbox export.");
    }
    if (dataset.schemaVersion !== await this.storage.getSchemaVersion()) {
      throw new Error("Inbox import schema does not match this app version.");
    }

    const blobById = new Map(dataset.blobs.map((item) => [item.ref.id, item] as const));
    const referencedBlobIds = new Set<string>();
    const current = await this.storage.documents.list({ includeDeleted: true, limit: 100_000 });
    const currentKeys = new Set(current.map((document) => `${document.type}\u0000${document.id}`));
    let liveEntries = 0;
    let tombstones = 0;
    let collisions = 0;
    for (const document of dataset.documents) {
      if (document.type !== INBOX_DOCUMENT_TYPE) throw new Error("Import contains data for another app.");
      const data = parseInboxEntryData(document.data);
      if (currentKeys.has(`${document.type}\u0000${document.id}`)) collisions += 1;
      if (document.deletedAt === null) {
        liveEntries += 1;
        if (data.attachment) {
          if (!blobById.has(data.attachment.blobId)) throw new Error("Import is missing an attachment blob.");
          referencedBlobIds.add(data.attachment.blobId);
          const portable = blobById.get(data.attachment.blobId)!;
          if (
            portable.ref.hash !== data.attachment.hash
            || portable.ref.mime !== data.attachment.mime
            || portable.ref.size !== data.attachment.size
            || portable.ref.createdAt !== data.attachment.createdAt
          ) throw new Error("Import attachment metadata does not match its blob.");
        }
      } else {
        tombstones += 1;
      }
    }
    const summary = summarizeDataset(dataset);
    return {
      ...summary,
      exportedAt: dataset.exportedAt,
      liveEntries,
      tombstones,
      collisions,
      orphanBlobs: dataset.blobs.filter((item) => !referencedBlobIds.has(item.ref.id)).length,
    };
  }

  async importDataset(datasetInput: unknown, policy: InboxImportPolicy): Promise<InboxImportResult> {
    if (policy !== "merge" && policy !== "replace") throw new Error("Choose Merge or Replace before importing.");
    const preview = await this.previewImport(datasetInput);
    const candidate = cloneImportInput(datasetInput) as PortableDataset;
    let importable = candidate;
    if (policy === "merge" && preview.collisions > 0) {
      const current = await this.storage.documents.list({ includeDeleted: true, limit: 100_000 });
      const currentKeys = new Set(current.map((document) => `${document.type}\u0000${document.id}`));
      const documents = candidate.documents.filter((document) => !currentKeys.has(`${document.type}\u0000${document.id}`));
      importable = {
        ...candidate,
        documents,
        outbox: [],
        // Blob IDs are content-addressed. Keeping the complete, already validated
        // blob set preserves portable orphan data without overwriting a document.
        blobs: candidate.blobs,
      };
    }
    const imported = await this.storage.importDataset(importable, { mode: policy });
    return { policy, preview, imported, skippedCollisions: policy === "merge" ? preview.collisions : 0 };
  }

  async ownershipSummary(): Promise<InboxOwnershipSummary> {
    return summarizeDataset(await this.storage.exportDataset());
  }

  async deleteAll(confirmation: string): Promise<InboxDeleteAllResult> {
    if (confirmation !== DELETE_ALL_CONFIRMATION) throw new Error("Delete-all confirmation is required.");
    const recoveryDataset = await this.storage.exportDataset();
    const removed = summarizeDataset(recoveryDataset);
    const empty: PortableDataset = {
      format: "tchoma-storage",
      formatVersion: 1,
      schemaVersion: recoveryDataset.schemaVersion,
      exportedAt: new Date().toISOString(),
      mode: recoveryDataset.mode,
      documents: [],
      outbox: [],
      blobs: [],
    };
    await this.storage.importDataset(empty, { mode: "replace" });
    const after = summarizeDataset(await this.storage.exportDataset());
    const cleared = after.documents === 0 && after.outboxOperations === 0 && after.blobs === 0;
    return { recoveryDataset, removed, cleared };
  }

  async readAttachment(entry: InboxEntry): Promise<Blob | null> {
    const ref = attachmentRef(entry);
    return ref ? this.storage.blobs.get(ref) : null;
  }

  diagnostics() {
    return this.storage.diagnostics({ requestPersistence: true });
  }

  close(): void {
    this.storage.close();
  }
}

function cloneImportInput(input: unknown): unknown {
  try {
    return structuredClone(input);
  } catch {
    throw new Error("Import could not be read as portable data.");
  }
}

function summarizeDataset(dataset: PortableDataset): InboxOwnershipSummary {
  let liveEntries = 0;
  let tombstones = 0;
  for (const document of dataset.documents) {
    if (document.deletedAt === null) liveEntries += 1;
    else tombstones += 1;
  }
  return {
    documents: dataset.documents.length,
    liveEntries,
    tombstones,
    outboxOperations: dataset.outbox.length,
    blobs: dataset.blobs.length,
    blobBytes: dataset.blobs.reduce((total, item) => total + item.ref.size, 0),
  };
}

let browserRepository: Promise<InboxRepository> | undefined;

export function openInboxRepository(): Promise<InboxRepository> {
  browserRepository ??= openIndexedDbStorage({
    databaseName: "tchoma-inbox-v1",
    mode: "local-only",
  }).then((storage) => new InboxRepository(storage));
  return browserRepository;
}
